import { ValidatorFn, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import {
    IConfigControlDuration,
    IConfigControlTextEditor,
    IConfigControlWithDataSource,
    IConfigControlWithTooltip,
    IControlType,
    IControlWithTooltip,
    IDataSourceQuery,
    IDurationControl,
    INumericControl,
    ISimpleControl,
    ISimpleDataSourceControl,
    ITextEditorControl,
} from '@se/cp.common';
import { DataBinding, DURATION_PLACEHOLDERS, DurationFormat } from '@se/se.ui.html5';
import { MenuConfiguration, MenuEnum } from '@se/ui.text.editor';
import { intersectionBy, isEmpty } from 'lodash-es';
import moment, { Moment } from 'moment';
import { Observable, of } from 'rxjs';
import { distinctUntilChanged, map, tap } from 'rxjs/operators';

import { CpDatasourceService } from '../../cp-datasource.service';
import { CpModelService } from '../../cp-model.service';
import { rowProportional } from '../utils/control-grid';
import { AdditionalContentControlType, createAdditionalContent } from './cp-additional-content';
import { CpControl } from './cp-control';
import { CpEntityList, ICpEntity, isEntity, PropertyType } from './cp-entity';

type ConfigControlTypeWorkaround =
    | IConfigControlWithTooltip
    | IConfigControlWithDataSource
    | IConfigControlDuration
    | IConfigControlTextEditor;

export const isSingleDataBindingControl = (control: any): control is SingleDataBindingControl =>
    control instanceof SingleDataBindingControl;

export class SingleDataBindingControl extends CpControl {
    dataBinding: DataBinding;
    dataSource?: string;
    icon = '';
    isWithLabelOnTop: boolean;
    readonly: boolean;
    required: boolean;
    tooltip = '';
    isDynamicDataSource: boolean = false;

    protected readonly _heightHint: number;

    constructor(
        control:
            | ISimpleControl
            | ISimpleDataSourceControl
            | IDurationControl
            | ITextEditorControl
            | IControlWithTooltip
            | INumericControl,
        dataBindingContext?: DataBinding,
    ) {
        super(control);

        const config = control.config[0] as ConfigControlTypeWorkaround;

        if ('isDynamicDataSource' in config) {
            this.isDynamicDataSource = Boolean(config.isDynamicDataSource);
        }
        if ('idDataSource' in config) {
            this.dataSource = config.idDataSource;
        }
        if ('tooltip' in config) {
            this.tooltip = config.tooltip ?? '';
        }
        if ('icon' in config) {
            this.icon = config.icon ?? '';
        }

        this.isWithLabelOnTop = 'isWithLabelOnTop' in config ? config.isWithLabelOnTop ?? false : false;
        this.readonly = config.readOnly;
        this.required = config.required;
        this.dataBinding = new DataBinding(config.dataBinding, dataBindingContext);
        this._heightHint = this.calculateHeightHint();
    }

    canAlignToBottom() {
        switch (this.controlType) {
            case IControlType.CheckBox:
            case IControlType.ThreeStateCheckBox:
                return false;
            default:
                return true;
        }
    }

    getHeightHint() {
        return this._heightHint;
    }

    getEntityObservable(modelService: CpModelService) {
        return modelService.registerEntity(this.dataBinding.entity);
    }

    getInitialValue(modelService: CpModelService) {
        return modelService
            .getEntity(this.dataBinding.entity, true)
            ?.getValue(new DataBinding(this.dataBinding.propertyName));
    }

    getModelObservable(modelService: CpModelService) {
        /*se binding avanzato sottoscrivere come nel componente misure*/
        if (this.dataBinding.isAdvanced()) {
            return modelService.registerEntity(this.dataBinding.entity).pipe(
                map((model) => model?.getPropertyValue(this.dataBinding.propertyName)),
                distinctUntilChanged(),
            );
        }
        return modelService.register(this.dataBinding).pipe(distinctUntilChanged());
    }

    onModelChange(modelService: CpModelService, model: PropertyType, emitEvent = true) {
        const entity = modelService.getEntity(this.dataBinding.entity);
        const dataBinding = entity?.path?.addSimple(this.dataBinding.propertyName);
        if (!dataBinding) {
            return false;
        }

        return modelService.setValue(dataBinding, model, { emitEvent });
    }

    private calculateHeightHint() {
        switch (this.controlType) {
            case IControlType.Autocomplete:
            case IControlType.Date:
            case IControlType.Duration:
            case IControlType.Money:
            case IControlType.NumericDecimal:
            case IControlType.NumericInteger:
            case IControlType.TextSingleLine:
                return 66;
            case IControlType.CheckBox:
            case IControlType.ThreeStateCheckBox:
                return 45;
            case IControlType.Combo:
            case IControlType.ComboMultiSelector:
            case IControlType.ComboMultiSelectorWithFilter:
            case IControlType.ComboWithFilter:
                return 66;
            case IControlType.DateTime:
            case IControlType.Time:
                return 66;
            case IControlType.TextMultiLine:
            case IControlType.Selector:
            case IControlType.TextFormatted:
                return rowProportional(this.rowSpan);
            default:
                return 0;
        }
    }
}

type CpComboModelType = CpEntityList | ICpEntity | PropertyType | undefined;

export class CpComboControl extends SingleDataBindingControl {
    readonly controlTypeGroup = 'Combo';

    dataSource!: string;
    dataSourceService!: CpDatasourceService;
    modelService!: CpModelService;
    queries?: IDataSourceQuery[];

    constructor(control: ISimpleDataSourceControl, dataBindingContext?: DataBinding) {
        super(control, dataBindingContext);
        this.queries = control.config[0].queries?.filter((query) => Boolean(query.propertyName)) ?? [];
    }

    get isMultiple() {
        return (
            this.controlType === IControlType.ComboMultiSelector ||
            this.controlType === IControlType.ComboMultiSelectorWithFilter
        );
    }

    get hasFilter() {
        return (
            this.controlType === IControlType.ComboWithFilter ||
            this.controlType === IControlType.ComboMultiSelectorWithFilter
        );
    }

    getInitialValue() {
        const propertyValue = this.modelService
            .getEntity(this.dataBinding.entity, true)
            ?.getValue(new DataBinding(this.dataBinding.propertyName));
        if (!propertyValue) {
            return null;
        }

        if (propertyValue instanceof CpEntityList) {
            return propertyValue.activeEntities;
        }

        /*92913*/
        if (!isEntity(propertyValue) && !this.isDynamicDataSource) {
            console.error(`Binding itemSources to non-entities is not supported anymore (Path: ${this.dataBinding})`);
            return null;
        }

        return propertyValue;
    }

    getModelObservable() {
        let observable: Observable<CpComboModelType> = this.isMultiple
            ? this.modelService.registerList(this.dataBinding)
            : this.modelService.registerEntity(this.dataBinding);

        return observable.pipe(
            distinctUntilChanged((a, b) => CpComboControl.areValuesDistinct(a, b)),
            map((a) => CpComboControl.transformInputModel(a)),
        );
    }

    getModelDynamicObservable() {
        return this.modelService.register(this.dataBinding).pipe(distinctUntilChanged());
    }

    private static areValuesDistinct(a: CpComboModelType, b: CpComboModelType): boolean {
        if (a instanceof CpEntityList && b instanceof CpEntityList) {
            const ae = a.activeEntities;
            const be = b.activeEntities;

            return ae.length === be.length && intersectionBy(ae, be, 'Id').length === ae.length;
        }
        if (isEntity(a) && isEntity(b)) {
            return a.Id === b.Id;
        }
        return false;
    }

    private static transformInputModel(model: PropertyType | undefined): ICpEntity[] {
        if (!model) {
            return [];
        }
        if (model instanceof CpEntityList) {
            return model.activeEntities;
        } else if (isEntity(model)) {
            return [model];
        }

        console.error(`Invalid model detected for ${this.name}`);

        return [];
    }
}

export class CpDateTimeControl extends SingleDataBindingControl {
    readonly controlTypeGroup = 'DateTime';

    getModelObservable(modelService: CpModelService) {
        return modelService.register(this.dataBinding).pipe(
            map(this.transformInputModel),
            distinctUntilChanged((a, b) => {
                if (a === null || b === null) {
                    return a === b;
                }
                return a.isSame(b);
            }),
        );
    }

    private transformInputModel(model: PropertyType | undefined): Moment | null {
        if (!model) {
            return null;
        } else if (moment.isMoment(model)) {
            return model;
        } else if (typeof model === 'string') {
            // Se il modello è una stringa, viene applicata la correzione del fuso perché si presume arrivi dal server.
            const correctDate = moment(model).utc();
            return correctDate.add(correctDate.utcOffset(), 'minute');
        }

        return null;
    }
}

export class CpInputControl extends SingleDataBindingControl {
    readonly controlTypeGroup = 'Input';
}

interface NumericValues {
    strModel: string | null;
    numModel: number | null;
    separator: string | null;
}

export class CpNumericControl extends SingleDataBindingControl {
    readonly controlTypeGroup = 'Numeric';

    private static readonly PATTERNS = {
        MONEY: '^([+\\-])?(\\d+)?([.,])?(\\d{1,2})?$',
        NUMERIC_DECIMAL: '^([+\\-])?(\\d+)?([\\.,])?(\\d+)?$',
        NUMERIC_INTEGER: '^[+\\-]?\\d+$',
    };

    max?: number;
    min?: number;

    pattern: string;

    constructor(control: INumericControl, dataBindingContext?: DataBinding) {
        super(control, dataBindingContext);
        this.max = control.config[0].max ?? undefined;
        this.min = control.config[0].min ?? undefined;

        switch (control.controlType) {
            case IControlType.Money:
                this.pattern = CpNumericControl.PATTERNS.MONEY;
                break;
            case IControlType.NumericDecimal:
                this.pattern = CpNumericControl.PATTERNS.NUMERIC_DECIMAL;
                break;
            case IControlType.NumericInteger:
                this.pattern = CpNumericControl.PATTERNS.NUMERIC_INTEGER;
                break;
        }
    }

    isFloatingPoint() {
        return this.controlType === IControlType.NumericDecimal || this.controlType === IControlType.Money;
    }

    getValidators() {
        let validators = [];

        if (this.max !== undefined) {
            validators.push(Validators.max(this.max));
        }
        if (this.min !== undefined) {
            validators.push(Validators.min(this.min));
        }

        return validators;
    }

    getValues(input: string | null): NumericValues {
        if (!input) {
            return { numModel: null, separator: null, strModel: null };
        }

        const matches = input.trim().match(new RegExp(this.pattern));
        if (!matches) {
            return { numModel: null, separator: null, strModel: input };
        }

        if (this.isFloatingPoint()) {
            return { numModel: parseFloat(matches[0].replace(',', '.')), separator: matches[3], strModel: matches[0] };
        }
        return { numModel: parseInt(matches[0], 10), separator: null, strModel: matches[0] };
    }
}

export class CpDurationControl extends CpInputControl {
    readonly format: DurationFormat;
    label?: string;

    constructor(control: IDurationControl, dataBindingContext?: DataBinding) {
        super(control, dataBindingContext);

        this.format = control.config[0].format;
    }

    init(translate: TranslateService) {
        this.label = this.createLabel(translate);
    }

    private createLabel(translate: TranslateService) {
        let label = '';

        if (this.format.months) {
            label = translate.instant(DURATION_PLACEHOLDERS.months) + '-';
        }
        if (this.format.days) {
            label += translate.instant(DURATION_PLACEHOLDERS.days) + '-';
        }
        if (this.format.hours) {
            label += translate.instant(DURATION_PLACEHOLDERS.hours) + ':';
        }
        if (this.format.minutes) {
            label += translate.instant(DURATION_PLACEHOLDERS.minutes) + ':';
        }
        if (this.format.seconds) {
            label += translate.instant(DURATION_PLACEHOLDERS.seconds) + ':';
        }

        // Rimuovi il ':' finale.
        return label.substring(0, label.length - 1);
    }
}

export interface IDefaultTextConfig {
    readonly defaultTextGroupName: string;
    readonly defaultTextName: string;
}

export class CpTextEditorControl extends SingleDataBindingControl {
    readonly controlTypeGroup = 'TextEditor';

    readonly additionalContent: AdditionalContentControlType[];
    readonly isToggleEnabled: boolean;
    readonly isWithLabelInToolbar: boolean;
    readonly menuConfiguration: IConfigControlTextEditor['toolbar'];
    readonly toggleDataBinding?: DataBinding;
    readonly suggestions?: string; //DGS lasciato per retrocompatibilità
    readonly defaultTextEnabled: boolean;
    readonly defaultText?: IDefaultTextConfig;

    private isEmpty = true;
    private isVisible = true;

    constructor(control: ITextEditorControl, dataBindingContext?: DataBinding) {
        super(control, dataBindingContext);

        const config = control.config[0];
        this.isToggleEnabled = !isEmpty(config.toggleDataBinding);
        this.menuConfiguration = config.toolbar;
        this.defaultTextEnabled = config.enableDefaultText ?? false;
        if (config.enableDefaultText) {
            if (!config.defaultTextGroupName || !config.defaultTextName) {
                throw new Error(
                    'Richiesta Abilitazione DefaultText senza indicare defaultTextGroupName o defaultTextName',
                );
            }
            this.defaultText = {
                defaultTextGroupName: config.defaultTextGroupName,
                defaultTextName: config.defaultTextName,
            };
        }
        if (config.suggestions) {
            // DGS lasciato per retrocompatibilità
            this.suggestions = config.suggestions ?? undefined;
            this.defaultText = {
                defaultTextGroupName: 'dataSource',
                defaultTextName: 'text',
            };
        }
        this.isWithLabelInToolbar = config.isWithLabelInToolbar ?? false;
        this.toggleDataBinding = config.toggleDataBinding
            ? new DataBinding(config.toggleDataBinding, dataBindingContext)
            : undefined;

        this.additionalContent = createAdditionalContent(control.additionalContent ?? [], dataBindingContext);
    }

    getValidators(modelService: CpModelService): ValidatorFn[] {
        return [
            (_) => {
                const entity = modelService.getEntity(this.dataBinding.entity);
                const isInvalid = this.required && this.isVisible && this.isEmpty;

                if (
                    entity &&
                    entity.setPropertyErrorStatus &&
                    entity.getPropertyErrorStatus &&
                    entity.getPropertyErrorStatus(this.dataBinding.propertyName, 'isEmpty') !== isInvalid
                ) {
                    entity.setPropertyErrorStatus(this.dataBinding.propertyName, 'isEmpty', !isInvalid);
                }

                return isInvalid ? { isEmpty: true } : null;
            },
        ];
    }

    getTitle() {
        return this.isWithLabelInToolbar ? this.name : undefined;
    }

    configureToolbar(): Map<MenuEnum, MenuConfiguration> {
        return this.menuConfiguration === 'light' ? TEXT_EDITOR_TOOLBAR_LIGHT : TEXT_EDITOR_TOOLBAR_FULL;
    }

    getHeightPxObservable(modelService: CpModelService): Observable<string> {
        if (this.toggleDataBinding) {
            return modelService.register(this.toggleDataBinding).pipe(map((v) => this.calculateHeight(!!v)));
        } else {
            return of(this.calculateHeight(true));
        }
    }

    getVisibilityObservable(modelService: CpModelService): Observable<boolean> {
        if (this.toggleDataBinding) {
            return modelService.register(this.toggleDataBinding).pipe(
                map((v) => !!v),
                distinctUntilChanged(),
                tap((v) => (this.isVisible = v)),
            );
        } else {
            return of(true);
        }
    }

    setIsEmpty(empty: boolean) {
        this.isEmpty = empty;
    }

    setHeight(height: number) {
        this.height = Math.max(80, height);
    }

    private calculateHeight(isVisible: boolean): string {
        if (!isVisible) {
            return 'auto';
        }

        return `${this.height}px`;
    }
}

const TEXT_EDITOR_TOOLBAR_LIGHT = new Map([
    [MenuEnum.SUGGESTIONS, { visible: true }],
    [MenuEnum.NEW, { visible: false }],
    [MenuEnum.UNDO, { visible: true }],
    [MenuEnum.REDO, { visible: true }],
    [MenuEnum.TEXT_FONT_FAMILY, { visible: false }],
    [MenuEnum.TEXT_FONT_SIZE, { visible: false }],
    [MenuEnum.TEXT_ALIGNMENT, { visible: false }],
    [MenuEnum.TEXT_FONT_WEIGHT, { visible: true }],
    [MenuEnum.TEXT_FONT_STYLE, { visible: true }],
    [MenuEnum.TEXT_DECORATION, { visible: true }],
    [MenuEnum.UNORDERED_LIST, { visible: false }],
    [MenuEnum.ORDERED_LIST, { visible: false }],
    [MenuEnum.MICROPHONE, { visible: false }],
]);

const TEXT_EDITOR_TOOLBAR_FULL = new Map([
    [MenuEnum.SUGGESTIONS, { visible: true }],
    [MenuEnum.NEW, { visible: false }],
    [MenuEnum.UNDO, { visible: true }],
    [MenuEnum.REDO, { visible: true }],
    [MenuEnum.TEXT_FONT_FAMILY, { visible: true }],
    [MenuEnum.TEXT_FONT_SIZE, { visible: true }],
    [MenuEnum.TEXT_ALIGNMENT, { visible: false }],
    [MenuEnum.TEXT_FONT_WEIGHT, { visible: true }],
    [MenuEnum.TEXT_FONT_STYLE, { visible: true }],
    [MenuEnum.TEXT_DECORATION, { visible: true }],
    [MenuEnum.UNORDERED_LIST, { visible: true }],
    [MenuEnum.ORDERED_LIST, { visible: true }],
    [MenuEnum.MICROPHONE, { visible: false }],
]);

export class CpSelectorControl extends SingleDataBindingControl {
    readonly controlTypeGroup = 'Selector';
    readonly additionalContent: AdditionalContentControlType[];

    constructor(control: ISimpleDataSourceControl, dataBindingContext?: DataBinding) {
        super(control, dataBindingContext);

        this.additionalContent = createAdditionalContent(control.additionalContent ?? [], dataBindingContext);
    }

    setHeight(height: number) {
        this.height = height;
    }
}

export class CpAutocompleteControl extends SingleDataBindingControl {
    readonly controlTypeGroup = 'Autocomplete';

    dataSource!: string;
    dataSourceService!: CpDatasourceService;
    modelService!: CpModelService;
}
